var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import REMOVE_MODEL_SPECIFICATION from 'api/mutations/REMOVE_MODEL_SPECIFICATION';
import GET_SPECIFICATIONS from 'api/queries/GET_SPECIFICATIONS';
import { SPECIFICATIONS_PAGE_SIZE } from 'constants/pagination';
import { splitStatusFilter } from 'utils/specificationStatusFilter';
import client from '../api/environment';
import { setSpecificationStateToProduction } from './specificationDetailsSlice';
const initialState = {
    filters: {
        search: '',
        tags: null,
        orderBy: '',
        statusFilter: [],
    },
    items: [],
    pageInfo: {
        hasNextPage: true,
        endCursor: null,
    },
    status: {
        fetch: 'idle',
        move: 'idle',
        remove: 'idle',
    },
    errors: {
        fetch: null,
        move: null,
        remove: null,
    },
    refetchFlags: {
        fetch: false,
        move: false,
        remove: false,
    },
};
export const fetchSpecifications = createAsyncThunk('specifications/fetchSpecifications', (arg, { getState, rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const state = getState();
    const { filters, pageInfo } = state.specifications;
    const overrideStates = (_a = arg === null || arg === void 0 ? void 0 : arg.states) !== null && _a !== void 0 ? _a : null;
    const { state: statusFilterState, statusId: statusFilterStatusId } = overrideStates
        ? { state: overrideStates, statusId: null }
        : splitStatusFilter(filters.statusFilter);
    try {
        const response = yield client.query({
            query: GET_SPECIFICATIONS,
            variables: {
                search: filters.search || null,
                tags: filters.tags,
                orderBy: filters.orderBy,
                first: SPECIFICATIONS_PAGE_SIZE,
                after: pageInfo.endCursor,
                state: statusFilterState,
                statusId: statusFilterStatusId,
            },
            fetchPolicy: 'network-only',
        });
        return response.data.me.modelSpecifications;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
export const removeSpecification = createAsyncThunk('specificationDetails/removeSpecification', ({ modelSpecificationId }, { rejectWithValue }) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const specifiactionData = yield client.mutate({
            mutation: REMOVE_MODEL_SPECIFICATION,
            variables: { modelSpecificationId },
        });
        return modelSpecificationId;
    }
    catch (error) {
        const apolloError = error;
        return rejectWithValue(apolloError.message);
    }
}));
const specificationsSlice = createSlice({
    name: 'specifications',
    initialState,
    reducers: {
        setRefetchFlag(state, action) {
            const { flag, value } = action.payload;
            state.refetchFlags[flag] = value;
        },
        setFilter(state, action) {
            const { filterName, value } = action.payload;
            state.filters[filterName] = value;
        },
        resetFilters(state) {
            state.filters = {
                search: '',
                tags: null,
                orderBy: '',
                statusFilter: [],
            };
        },
        toggleStatusFilter(state, action) {
            const value = action.payload;
            state.filters.statusFilter = state.filters.statusFilter.includes(value)
                ? state.filters.statusFilter.filter((item) => item !== value)
                : [...state.filters.statusFilter, value];
        },
        resetStatusFilter(state) {
            state.filters.statusFilter = [];
        },
        resetSpecifications(state) {
            state.items = [];
            state.pageInfo = {
                hasNextPage: true,
                endCursor: null,
            };
        },
        addSpecListPhoto(state, action) {
            const { id, image } = action.payload;
            const index = state.items.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.items[index].image = image;
            }
        },
        removeSpecListPhoto(state, action) {
            const { id } = action.payload;
            const index = state.items.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.items[index].image = null;
            }
        },
        updateSpecListStatus(state, action) {
            const { id, status } = action.payload;
            const index = state.items.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.items[index].status = status;
            }
        },
        updateSpecListState(state, action) {
            const { id, state: newState } = action.payload;
            const index = state.items.findIndex((s) => s.id === id);
            if (index !== -1) {
                state.items[index].state = newState;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSpecifications.pending, (state) => {
            state.status.fetch = 'loading';
            state.refetchFlags.fetch = false;
        })
            .addCase(fetchSpecifications.fulfilled, (state, action) => {
            var _a, _b;
            const newSpecifications = (_a = action.payload.edges) === null || _a === void 0 ? void 0 : _a.map((edge) => edge.node);
            const uniqueSpecifications = newSpecifications.filter((newSpecification) => !state.items.some((existingSpecification) => existingSpecification.id === newSpecification.id));
            state.items = [...state.items, ...uniqueSpecifications];
            state.pageInfo = (_b = action.payload) === null || _b === void 0 ? void 0 : _b.pageInfo;
            state.status.fetch = 'succeeded';
        })
            .addCase(fetchSpecifications.rejected, (state, action) => {
            state.status.fetch = 'failed';
            state.errors.fetch = action.error.message || 'Error fetching specifications';
        })
            .addCase(setSpecificationStateToProduction.fulfilled, (state, action) => {
            const { id, state: specificationState } = action.payload;
            const specification = state.items.find((item) => item.id === id);
            if (specification) {
                specification.state = specificationState;
            }
            state.refetchFlags.fetch = true;
        })
            .addCase(removeSpecification.pending, (state) => {
            state.status.remove = 'loading';
        })
            .addCase(removeSpecification.fulfilled, (state, action) => {
            state.items = state.items.filter((specification) => specification.id !== action.payload);
            state.status.remove = 'succeeded';
            state.refetchFlags.fetch = true;
        })
            .addCase(removeSpecification.rejected, (state, action) => {
            state.errors.remove = action.error.message || 'Error removing specification';
            state.status.remove = 'failed';
            state.refetchFlags.fetch = true;
        });
    },
});
export const { setRefetchFlag, setFilter, resetFilters, toggleStatusFilter, resetStatusFilter, resetSpecifications, addSpecListPhoto, removeSpecListPhoto, updateSpecListStatus, updateSpecListState, } = specificationsSlice.actions;
export const selectSpecificationsStatus = (state) => state.specifications.status;
export const selectFilters = (state) => state.specifications.filters;
export default specificationsSlice.reducer;
